import { useCallback, useEffect, useState } from "react";
import Helmet from "react-helmet";
import { Outlet, Route } from "react-router";
import styled from "styled-components";

import Applications from "./app/Applications";
import Template from "./app/Template";
import { listTemplates, type DataProperty, type TemplateMetas, type UsageOption } from "../../clients/documentExports/api";
import AppHeader from "../../components/AppHeader";
import useNotifications, { NotificationsProvider } from "../../components/Notifications";

const Styled = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 4vw;
    padding: 128px 128px 64px 192px;

    & > *:not(:last-child):not(:hover):not(:has(input:focus)) {
        opacity: .4;
    }
`;

/** Shared by the application list (template markers) and the template panel (details, refresh). */
export interface DocumentExportsOutletContext {
    templates: TemplateMetas;
    /** The usages a template can belong to, as the service publishes them. */
    usages: UsageOption[];
    /** The per-application texts the service fills into every template (data.json), as it publishes them. */
    dataProperties: DataProperty[];
    loading: boolean;
    reload(): Promise<void>;
}

// Which applications have templates lives on the document-exports service, not in
// GraphQL, so it is fetched once here and refreshed after every upload. The service
// answers with the caller's applications only, per their PIM roles on the apps' brands.
function Shell() {
    const [templates, setTemplates] = useState<TemplateMetas>({});
    const [usages, setUsages] = useState<UsageOption[]>([]);
    const [dataProperties, setDataProperties] = useState<DataProperty[]>([]);
    const [loading, setLoading] = useState(true);
    const notifications = useNotifications();

    const reload = useCallback(
        async () => {
            setLoading(true);
            try {
                const listing = await listTemplates();
                setTemplates(listing.templates);
                setUsages(listing.usages);
                setDataProperties(listing.dataProperties);
            } catch (error) {
                notifications.error(`Could not load templates: ${ (error as Error).message }`);
            } finally {
                setLoading(false);
            }
        },
        [notifications],
    );

    useEffect(
        () => {
            reload();
        },
        [reload],
    );

    return (
        <Styled>
            <Outlet context={ { templates, usages, dataProperties, loading, reload } satisfies DocumentExportsOutletContext } />
        </Styled>
    );
}

function App() {
    return (
        <NotificationsProvider>
            <Helmet>
                <title>Document Exports</title>
            </Helmet>
            <AppHeader title={ <>document<br />exports</> } />
            <Shell />
        </NotificationsProvider>
    );
}

export default (
    <Route Component={ App } path="document-exports">
        <Route Component={ Applications }>
            <Route index />
            <Route Component={ Template } path=":applicationDomain" />
        </Route>
    </Route>
);
